// Lógica pura del modelo multi-hallazgo del checklist de mantención.
//
// Un ítem (actividad_respuesta) es una LISTA de hallazgos: N incidencias + N
// sugerencias. El estado vive en el provider con este shape:
//
//   obsSug[idRes] = {
//     incidencias: [{
//       tempId, idHallazgo?, descripcion, severidad?, idComponente?,
//       idHallazgoPrevio?,  // presente solo en una incidencia ARRASTRADA de una
//                           // visita anterior (ver addHallazgoArrastrado más abajo):
//                           // el backend la usa para encadenar el hallazgo en vez
//                           // de crear uno suelto. SIEMPRE viaja al PUT.
//       arrastradoDe?,      // metadato de presentación de esa misma incidencia
//                           // arrastrada ({ firstSeenAt, mantencionesVistas }); el
//                           // payload NUNCA lo emite (ver toPayloadHallazgo).
//     }],
//     sugerencias: [{ tempId, idHallazgo?, descripcion, idComponente? }],
//     eliminados:  [idHallazgo, ...]   // hallazgos existentes que el técnico quitó
//   }
//
// Identidad cliente: `tempId` (uuid) por hallazgo. El backend devuelve el mapeo
// tempId → idHallazgo en la respuesta del PUT; con eso (a) se suben las fotos al
// idHallazgo real y (b) se hace backfill de `idHallazgo` en obsSug para que un
// segundo save ACTUALICE (no duplique — el backend ya no deduplica por tipo).
//
// Todas las funciones son puras (sin React) → unit-testables en aislamiento.
import { v4 as uuidv4 } from 'uuid';

const TIPO_KEY = { INCIDENCIA: 'incidencias', SUGERENCIA: 'sugerencias' };

// Normaliza un item a {incidencias, sugerencias, eliminados} con arrays presentes.
function ensureItem(obsSug, idRes) {
  const cur = obsSug?.[idRes];
  return {
    incidencias: Array.isArray(cur?.incidencias) ? cur.incidencias : [],
    sugerencias: Array.isArray(cur?.sugerencias) ? cur.sugerencias : [],
    eliminados: Array.isArray(cur?.eliminados) ? cur.eliminados : [],
  };
}

// Agrega un hallazgo VACÍO del tipo dado al ítem. Devuelve { obsSug, tempId }
// (el tempId se necesita afuera para keyear las fotos de esa fila nueva).
export function addHallazgo(obsSug, idRes, tipo, tempId = uuidv4()) {
  const key = TIPO_KEY[tipo];
  if (!key) return { obsSug: obsSug || {}, tempId: null };
  const base = obsSug || {};
  const item = ensureItem(base, idRes);
  const nuevo =
    tipo === 'INCIDENCIA'
      ? { tempId, descripcion: '', severidad: undefined, idComponente: null }
      : { tempId, descripcion: '', idComponente: null };
  return {
    obsSug: { ...base, [idRes]: { ...item, [key]: [...item[key], nuevo] } },
    tempId,
  };
}

// Aplica un patch (descripcion/severidad/idComponente) al hallazgo identificado
// por tempId del tipo dado. No-op si no existe.
export function updateHallazgo(obsSug, idRes, tipo, tempId, patch) {
  const key = TIPO_KEY[tipo];
  if (!key || !obsSug?.[idRes]) return obsSug;
  const item = ensureItem(obsSug, idRes);
  return {
    ...obsSug,
    [idRes]: {
      ...item,
      [key]: item[key].map((h) => (h.tempId === tempId ? { ...h, ...patch } : h)),
    },
  };
}

// Quita el hallazgo (por tempId). Si tenía `idHallazgo` (ya persistido) lo
// acumula en `eliminados` para que el PUT lo marque INACTIVA. Si era nuevo
// (solo tempId), simplemente desaparece de la lista.
export function removeHallazgo(obsSug, idRes, tipo, tempId) {
  const key = TIPO_KEY[tipo];
  if (!key || !obsSug?.[idRes]) return obsSug;
  const item = ensureItem(obsSug, idRes);
  const target = item[key].find((h) => h.tempId === tempId);
  const eliminados =
    target && target.idHallazgo ? [...item.eliminados, target.idHallazgo] : item.eliminados;
  return {
    ...obsSug,
    [idRes]: {
      ...item,
      [key]: item[key].filter((h) => h.tempId !== tempId),
      eliminados,
    },
  };
}

// Agrega al ítem la incidencia que arrastra un hallazgo de una visita anterior:
// precargada con su texto y criticidad, marcada con `idHallazgoPrevio` (el backend
// la usa para encadenar el hallazgo en vez de crear uno suelto) y con los datos de
// origen que la UI muestra. Es una incidencia normal: el técnico puede editarla.
export function addHallazgoArrastrado(obsSug, idRes, previo, tempId = uuidv4()) {
  const base = obsSug || {};
  const item = ensureItem(base, idRes);
  const nueva = {
    tempId,
    idHallazgoPrevio: previo.id,
    descripcion: previo.descripcion || '',
    severidad: previo.severidad ?? undefined,
    idComponente: previo.id_component ?? null,
    arrastradoDe: {
      firstSeenAt: previo.first_seen_at ?? null,
      mantencionesVistas: previo.mantencionesVistas ?? null,
    },
  };
  return {
    obsSug: { ...base, [idRes]: { ...item, incidencias: [...item.incidencias, nueva] } },
    tempId,
  };
}

function toPayloadHallazgo(h) {
  const out = { descripcion: h.descripcion ?? '' };
  if (h.tempId) out.tempId = h.tempId;
  if (h.idHallazgo) out.idHallazgo = h.idHallazgo;
  if (h.idHallazgoPrevio) out.idHallazgoPrevio = h.idHallazgoPrevio;
  if (h.severidad !== undefined) out.severidad = h.severidad;
  if (h.idComponente !== undefined) out.idComponente = h.idComponente;
  return out;
}

// Construye el `itemObs` del PUT desde obsSug: por idRes emite
// { incidencias, sugerencias, eliminados }. Omite ítems totalmente vacíos.
// Devuelve null si no hay nada que enviar (el backend omite el campo).
export function buildItemObsPayload(obsSug) {
  if (!obsSug || Object.keys(obsSug).length === 0) return null;
  const out = {};
  for (const [idRes, item] of Object.entries(obsSug)) {
    const incidencias = (item?.incidencias || []).map(toPayloadHallazgo);
    const sugerencias = (item?.sugerencias || []).map(toPayloadHallazgo);
    const eliminados = item?.eliminados || [];
    if (incidencias.length || sugerencias.length || eliminados.length) {
      out[idRes] = { incidencias, sugerencias, eliminados };
    }
  }
  return Object.keys(out).length ? out : null;
}

// Mergea el mapeo `tempId → idHallazgo` devuelto por el PUT al map existente
// (clave = tempId). Reemplaza a `hallazgosPorActRes`: ahora la foto se sube al
// idHallazgo resuelto por el tempId de su fila. Inmutable.
export function mergeHallazgosResponse(prevMap, hallazgos) {
  const next = new Map(prevMap);
  if (!Array.isArray(hallazgos)) return next;
  for (const h of hallazgos) {
    if (h && h.tempId != null && h.id_hallazgo != null) {
      next.set(String(h.tempId), Number(h.id_hallazgo));
    }
  }
  return next;
}

// Acceso tipado al map por tempId. Devuelve el idHallazgo o undefined.
export function getHallazgoId(map, tempId) {
  if (!map || tempId == null) return undefined;
  return map.get(String(tempId));
}

// Backfill: tras un save, escribe el `idHallazgo` real en cada hallazgo de
// obsSug cuyo `tempId` aparezca en la respuesta. Crítico: sin esto, un segundo
// save volvería a CREAR los mismos hallazgos (el backend ya no deduplica). Solo
// rellena los que aún no tienen idHallazgo. Inmutable; no-op si no hay mapeo.
export function applyHallazgosResponse(obsSug, hallazgos) {
  if (!obsSug || !Array.isArray(hallazgos) || hallazgos.length === 0) return obsSug;
  const byTemp = new Map();
  for (const h of hallazgos) {
    if (h && h.tempId != null && h.id_hallazgo != null) {
      byTemp.set(String(h.tempId), Number(h.id_hallazgo));
    }
  }
  if (byTemp.size === 0) return obsSug;
  const patchList = (list) =>
    (list || []).map((h) => {
      const id = h.tempId != null ? byTemp.get(String(h.tempId)) : undefined;
      return id && !h.idHallazgo ? { ...h, idHallazgo: id } : h;
    });
  const out = {};
  for (const [idRes, item] of Object.entries(obsSug)) {
    out[idRes] = {
      ...item,
      incidencias: patchList(item?.incidencias),
      sugerencias: patchList(item?.sugerencias),
    };
  }
  return out;
}

// Hidrata obsSug desde la lista plana de actividad_respuesta del GET. Cada
// act_res trae `hallazgos: [{ id, tipo, descripcion, severidad, id_component,
// estado }]` (solo los ACTIVA del checklist). Genera un tempId por fila para la
// UI y conserva el idHallazgo real. Devuelve null si no hay ninguno (el provider
// deja obsSug en null y cae al fallback legacy si aplica).
export function hydrateObsSugFromActividades(flatActividades, genTempId = uuidv4) {
  if (!Array.isArray(flatActividades)) return null;
  const obsSug = {};
  for (const a of flatActividades) {
    const idRes = a?.id;
    if (idRes == null) continue;
    const hallazgos = Array.isArray(a.hallazgos) ? a.hallazgos : [];
    const incidencias = [];
    const sugerencias = [];
    for (const h of hallazgos) {
      if (h.estado && h.estado !== 'ACTIVA') continue;
      const base = {
        tempId: genTempId(),
        idHallazgo: h.id,
        descripcion: h.descripcion || '',
        idComponente: h.id_component ?? null,
      };
      if (h.tipo === 'INCIDENCIA')
        incidencias.push({ ...base, severidad: h.severidad ?? undefined });
      else if (h.tipo === 'SUGERENCIA') sugerencias.push(base);
    }
    if (incidencias.length || sugerencias.length) {
      obsSug[idRes] = { incidencias, sugerencias, eliminados: [] };
    }
  }
  return Object.keys(obsSug).length ? obsSug : null;
}

// ¿Hay al menos una incidencia con texto pero sin severidad en este ítem? (para
// la validación de cierre en el cliente, espejo del gate backend). Recibe el
// item {incidencias} ya normalizado.
export function incidenciasIncompletas(item) {
  const incidencias = item?.incidencias || [];
  return incidencias.filter((h) => {
    const hasText = h.descripcion && String(h.descripcion).trim() !== '';
    return hasText && !h.severidad;
  });
}
